
import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Globe, Lock, Pencil, ArrowLeft } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import Header from "@/components/Header";
import { Skeleton } from "@/components/ui/skeleton";
import ImageCard from "@/components/ImageCard";

import { supabase } from "@/integrations/supabase/client";
import { Album, Image } from "@/lib/types";
import { useAuth } from "@/contexts/AuthContext";
import MetaHead from "@/components/MetaHead";

const AlbumView = () => {
  const { slug } = useParams<{ slug: string }>();
  const [album, setAlbum] = useState<Album | null>(null);
  const [images, setImages] = useState<Image[]>([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const { session, user } = useAuth();
  const navigate = useNavigate();
  const isOwner = album && session?.user && album.userId === session.user.id;

  // Fetch album data
  useEffect(() => {
    if (!slug) return;

    const fetchAlbum = async () => {
      setLoading(true);
      try {
        // Get album
        const { data: albumData, error: albumError } = await supabase
          .from('albums')
          .select('*')
          .eq('slug', slug)
          .single();

        if (albumError) {
          console.error('Error fetching album:', albumError);
          toast.error('Album not found');
          navigate('/dashboard');
          return;
        }

        // Increment album view count
        await supabase
          .from('albums')
          .update({ views: albumData.views + 1 })
          .eq('id', albumData.id);

        // Format album data
        const formattedAlbum: Album = {
          id: albumData.id,
          name: albumData.name,
          description: albumData.description || undefined,
          slug: albumData.slug,
          createdAt: new Date(albumData.created_at).getTime(),
          updatedAt: new Date(albumData.updated_at).getTime(),
          isPublic: albumData.is_public,
          views: albumData.views + 1,
          userId: albumData.user_id
        };

        setAlbum(formattedAlbum);

        // Get album images
        const { data: albumImages, error: albumImagesError } = await supabase
          .from('album_images')
          .select('image_id')
          .eq('album_id', albumData.id);

        if (albumImagesError) {
          console.error('Error fetching album images:', albumImagesError);
          setImages([]);
          setLoading(false);
          return;
        }

        if (!albumImages || albumImages.length === 0) {
          setImages([]);
          setLoading(false);
          return;
        }

        // Get image details
        const imageIds = albumImages.map(item => item.image_id);
        const { data: imagesData, error: imagesError } = await supabase
          .from('images')
          .select('*')
          .in('id', imageIds);

        if (imagesError) {
          console.error('Error fetching images:', imagesError);
          setImages([]);
          setLoading(false);
          return;
        }

        // Format image data
        const formattedImages = await Promise.all(imagesData.map(async (img) => {
          const { data: { publicUrl } } = supabase.storage
            .from('images')
            .getPublicUrl(img.storage_path);

          // Try to determine contentType from storage path
          let contentType: string | undefined = undefined;
          const extension = img.storage_path.split('.').pop()?.toLowerCase();
          
          if (extension) {
            if (['mp4', 'webm', 'mov'].includes(extension)) {
              contentType = `video/${extension === 'mov' ? 'quicktime' : extension}`;
            } else if (extension === 'gif') {
              contentType = 'image/gif';
            } else if (['jpg', 'jpeg', 'png', 'webp'].includes(extension)) {
              contentType = `image/${extension === 'jpg' ? 'jpeg' : extension}`;
            }
          }

          return {
            id: img.id,
            name: img.name,
            description: img.description || undefined,
            url: publicUrl,
            slug: img.slug,
            createdAt: new Date(img.created_at).getTime(),
            views: img.views,
            contentType
          };
        }));

        setImages(formattedImages);
      } catch (error) {
        console.error('Error fetching album data:', error);
        toast.error('Failed to load album');
      } finally {
        setLoading(false);
      }
    };

    fetchAlbum();
  }, [slug, navigate]);

  const handleTogglePublic = async () => {
    if (!album) return;

    try {
      const { error } = await supabase
        .from('albums')
        .update({ is_public: !album.isPublic })
        .eq('id', album.id);

      if (error) throw error;

      setAlbum({
        ...album,
        isPublic: !album.isPublic
      });

      toast.success(`Album is now ${!album.isPublic ? 'public' : 'private'}`);
    } catch (error) {
      console.error('Error updating album visibility:', error);
      toast.error('Failed to update album visibility');
    }
  };

  const handleRemoveImage = async (imageId: string) => {
    if (!album) return;

    try {
      const { error } = await supabase
        .from('album_images')
        .delete()
        .eq('album_id', album.id)
        .eq('image_id', imageId);

      if (error) throw error;

      // Update local state
      setImages(images.filter(img => img.id !== imageId));
      toast.success('Image removed from album');
    } catch (error) {
      console.error('Error removing image from album:', error);
      toast.error('Failed to remove image from album');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen gradient-bg">
        <Header />
        <div className="container mx-auto pt-24 pb-12 px-4">
          <Skeleton className="h-10 w-1/3 mb-2" />
          <Skeleton className="h-6 w-1/2 mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="aspect-video">
                <Skeleton className="h-full w-full" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!album) {
    return (
      <div className="min-h-screen gradient-bg">
        <Header />
        <div className="container mx-auto pt-24 pb-12 px-4 text-center">
          <h1 className="text-2xl font-bold mb-4">Album Not Found</h1>
          <p className="text-white/70 mb-6">The album you're looking for doesn't exist or has been removed.</p>
          <Button asChild>
            <Link to="/dashboard">Go to Dashboard</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <MetaHead
        title={album.name}
        description={album.description || `View ${album.name} - an image collection`}
        url={`${window.location.origin}/album/${album.slug}`}
        imageUrl={images.length > 0 ? images[0].url : undefined}
      />
      <div className="min-h-screen gradient-bg">
        <Header />
        <div className="container mx-auto pt-24 pb-12 px-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white/70 hover:text-white"
                  asChild
                >
                  <Link to="/dashboard">
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Back
                  </Link>
                </Button>
              </div>
              <h1 className="text-2xl font-bold">{album.name}</h1>
              {album.description && (
                <p className="text-white/70 mt-1">{album.description}</p>
              )}
              <div className="flex items-center gap-4 mt-2 text-sm text-white/60">
                <span>{images.length} {images.length === 1 ? 'image' : 'images'}</span>
                <span>{album.views} {album.views === 1 ? 'view' : 'views'}</span>
                <span className="flex items-center">
                  {album.isPublic ? (
                    <>
                      <Globe className="h-3 w-3 mr-1" />
                      Public
                    </>
                  ) : (
                    <>
                      <Lock className="h-3 w-3 mr-1" />
                      Private
                    </>
                  )}
                </span>
              </div>
            </div>
            
            {isOwner && (
              <div className="flex items-center gap-2">
                {isEditing ? (
                  <Card className="bg-card/50 backdrop-blur-sm border-white/10 p-3">
                    <CardContent className="p-0 flex items-center gap-3">
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="public-mode"
                          checked={album.isPublic}
                          onCheckedChange={handleTogglePublic}
                        />
                        <label htmlFor="public-mode" className="text-sm cursor-pointer">
                          {album.isPublic ? 'Public' : 'Private'}
                        </label>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsEditing(false)}
                      >
                        Done
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsEditing(true)}
                  >
                    <Pencil className="h-4 w-4 mr-2" />
                    Edit Album
                  </Button>
                )}
              </div>
            )}
          </div>

          {images.length === 0 ? (
            <div className="text-center py-12 my-4 border border-white/10 rounded-lg bg-black/20">
              <h3 className="text-lg font-medium">No images in this album</h3>
              {isOwner && (
                <p className="text-white/60 mt-2 mb-4">
                  Add images to this album from your dashboard
                </p>
              )}
              {isOwner && (
                <Button asChild>
                  <Link to="/dashboard">Go to Dashboard</Link>
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {images.map((image) => (
                <ImageCard
                  key={image.id}
                  image={image}
                  onDelete={isOwner ? () => handleRemoveImage(image.id) : () => {}}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AlbumView;
